import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, ProgressBar, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RotateCw, Send, Trash2 } from "lucide-react";
import Axios, { authHeaders } from "../../utils/axios";
import { useToast } from "../../context/TostContext";
import { ImportStatusChip, formatDate } from "./shared";

/**
 * One bulk import: progress while it runs, then every sheet's result to
 * review — how many zones got a code, how many parcels a survey number, how
 * well the control points fit — with failures and doubtful sheets first.
 * Open a sheet to fix it like a single import; publish the built ones together.
 */

const POLL_MS = 3000;
const DONE = ["built", "published", "failed"];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "check", label: "To check" },
  { key: "built", label: "Built" },
  { key: "failed", label: "Failed" },
  { key: "working", label: "In progress" },
  { key: "published", label: "Published" },
];

const pct = (part, whole) => (whole ? Math.round((100 * part) / whole) : null);
const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("en-IN"));

/** Why a built sheet deserves a look before publishing; null when it looks fine. */
function concerns(s) {
  if (s.status !== "built") return null;
  const reasons = [];
  if (!s.zones) reasons.push("no zones");
  else if (pct(s.zones_coded, s.zones) < 95) reasons.push(`${100 - pct(s.zones_coded, s.zones)}% of zones without a code`);
  if (s.parcels && pct(s.parcels_numbered, s.parcels) < 70) reasons.push(`only ${pct(s.parcels_numbered, s.parcels)}% of parcels numbered`);
  if (s.rms_m != null && s.rms_m > 20) reasons.push(`control points fit to ${s.rms_m} m`);
  return reasons.length ? reasons.join(", ") : null;
}

export default function BatchDetail() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [batch, setBatch] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Sheets marked to check stay drafts unless the admin says otherwise.
  const [leaveOutDoubtful, setLeaveOutDoubtful] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await Axios.get(`/maps/gis-imports/batches/${batchId}`, authHeaders());
      setBatch(res.data?.data);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.response?.data?.message || "Could not load the bulk import");
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (batch?.status !== "running") return undefined;
    const timer = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer);
  }, [batch, load]);

  const sheets = useMemo(() => {
    const list = (batch?.sheets || []).map((s) => ({ ...s, concern: concerns(s) }));
    // Failed, then doubtful, then being worked on, then fine, then waiting.
    const rank = (s) =>
      s.status === "failed" ? 0 : s.concern ? 1 : s.status === "queued" ? 4 : DONE.includes(s.status) ? 3 : 2;
    return list.sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title, undefined, { numeric: true }));
  }, [batch]);

  const shown = sheets.filter((s) => {
    if (filter === "all") return true;
    if (filter === "check") return s.status === "failed" || !!s.concern;
    if (filter === "working") return !DONE.includes(s.status);
    return s.status === filter;
  });

  const act = async (kind) => {
    setConfirm(null);
    setBusy(kind);
    try {
      if (kind === "publish") {
        const res = await Axios.post(
          `/maps/gis-imports/batches/${batchId}/publish`,
          { exclude_import_ids: leaveOutDoubtful ? doubtfulBuilt.map((s) => s.import_id) : [] },
          authHeaders()
        );
        const r = res.data?.data;
        toast.success(
          `Published ${r.published} sheet${r.published === 1 ? "" : "s"}.` +
            (r.left_out ? ` ${r.left_out} left as drafts to check.` : "")
        );
        if (r.failed?.length) toast.error(`${r.failed.length} could not be published: ${r.failed.map((f) => f.title).join(", ")}`);
        setBatch(r.batch);
      } else if (kind === "resume" || kind === "retry") {
        const res = await Axios.post(
          `/maps/gis-imports/batches/${batchId}/resume`,
          { retry_failed: kind === "retry" },
          authHeaders()
        );
        setBatch(res.data?.data);
      } else if (kind === "discard") {
        const res = await Axios.delete(`/maps/gis-imports/batches/${batchId}`, authHeaders());
        const r = res.data?.data;
        toast.success(`Discarded ${r.removed} draft${r.removed === 1 ? "" : "s"}.`);
        if (r.kept_published) await load();
        else navigate("/gis-imports");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "That did not work");
    } finally {
      setBusy(null);
    }
  };

  if (loadError) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Alert variant="danger" className="mb-0">{loadError}</Alert>
      </div>
    );
  }
  if (!batch) {
    return (
      <div className="bg-white rounded-2 common-shadow mt-4 p-4 text-center">
        <Spinner />
      </div>
    );
  }

  const c = batch.counts || {};
  const total = batch.sheets.length;
  const finished = (c.built || 0) + (c.published || 0) + (c.failed || 0);
  const builtCount = c.built || 0;
  const toCheck = sheets.filter((s) => s.status === "failed" || s.concern).length;
  const doubtfulBuilt = sheets.filter((s) => s.status === "built" && s.concern);
  const toPublish = builtCount - (leaveOutDoubtful ? doubtfulBuilt.length : 0);
  const running = batch.status === "running";
  const t = batch.totals || {};

  return (
    <>
      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <Button variant="link" className="p-0 d-flex align-items-center gap-1 mb-3" onClick={() => navigate("/gis-imports")}>
          <ArrowLeft size={16} /> All GIS imports
        </Button>
        <h5 className="fw-semibold mb-1">
          {batch.location?.name} · {batch.plan_name}
        </h5>
        <div className="text-muted small mb-3">
          {batch.authority} · {total} sheets · started {formatDate(batch.created_at)}
          {batch.finished_at && <> · finished {formatDate(batch.finished_at)}</>}
          {batch.template_import_id && (
            <>
              {" · "}
              <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => navigate(`/gis-imports/${batch.template_import_id}`)}>
                template
              </Button>
            </>
          )}
        </div>

        <ProgressBar className="mb-2" style={{ height: 10 }}>
          <ProgressBar variant="success" now={pct((c.built || 0) + (c.published || 0), total)} key="ok" />
          <ProgressBar variant="danger" now={pct(c.failed || 0, total)} key="failed" />
        </ProgressBar>
        <div className="small text-muted mb-3">
          {running && <Spinner size="sm" className="me-2" />}
          {finished} of {total} done · {fmt(c.built)} built · {fmt(c.published)} published · {fmt(c.failed)} failed
          {(c.queued || 0) > 0 && <> · {c.queued} waiting</>}
        </div>

        {batch.status === "paused" && (
          <Alert variant="warning" className="d-flex align-items-center justify-content-between gap-3 py-2">
            <span className="small">The server restarted while this was running. Resume to finish the remaining sheets.</span>
            <Button size="sm" variant="warning" className="d-flex align-items-center gap-1" disabled={!!busy} onClick={() => act("resume")}>
              {busy === "resume" ? <Spinner size="sm" /> : <RotateCw size={14} />} Resume
            </Button>
          </Alert>
        )}

        <div className="d-flex flex-wrap gap-4 small mb-1">
          <Stat label="Zones" value={t.zones} sub={`${pct(t.zones_coded, t.zones) ?? "-"}% with a code`} />
          <Stat label="Survey parcels" value={t.parcels} sub={`${pct(t.parcels_numbered, t.parcels) ?? "-"}% numbered`} />
          <Stat label="To check" value={toCheck} sub="failed or doubtful" />
        </div>
      </div>

      <div className="bg-white rounded-2 common-shadow mt-4 p-4">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "primary" : "outline-secondary"}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === "check" && toCheck > 0 && ` (${toCheck})`}
            </Button>
          ))}
        </div>

        <Table hover responsive size="sm" className="align-middle mb-0">
          <thead>
            <tr>
              <th>Sheet</th>
              <th>Status</th>
              <th className="text-end">Zones</th>
              <th className="text-end">Coded</th>
              <th className="text-end">Parcels</th>
              <th className="text-end">Numbered</th>
              <th className="text-end">Fit</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={8} className="text-muted small">Nothing here.</td>
              </tr>
            )}
            {shown.map((s) => (
              <tr key={s.import_id} role="button" style={{ cursor: "pointer" }} onClick={() => navigate(`/gis-imports/${s.import_id}`)}>
                <td className="fw-medium">{s.title}</td>
                <td>
                  {s.status === "queued" ? <span className="badge bg-light text-dark fw-medium rounded-1">Waiting</span> : <ImportStatusChip status={s.status} />}
                </td>
                <td className="text-end">{fmt(s.zones)}</td>
                <td className="text-end">{s.zones ? `${pct(s.zones_coded, s.zones)}%` : "-"}</td>
                <td className="text-end">{fmt(s.parcels)}</td>
                <td className="text-end">{s.parcels ? `${pct(s.parcels_numbered, s.parcels)}%` : "-"}</td>
                <td className="text-end text-nowrap">{s.rms_m != null ? `${s.rms_m} m` : "-"}</td>
                <td className={`small ${s.status === "failed" ? "text-danger" : s.concern ? "text-warning-emphasis" : "text-muted"}`}>
                  {s.status === "failed" ? s.error : s.concern || (s.stage && !DONE.includes(s.status) ? s.stage : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {batch.skipped?.length > 0 && (
          <details className="mt-3 small">
            <summary className="text-muted">{batch.skipped.length} sheets were not included</summary>
            <ul className="mb-0 mt-2">
              {batch.skipped.map((s) => (
                <li key={s.map_id}>
                  {s.title}: {s.reason}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {!running && (
        <div className="bg-white rounded-2 common-shadow mt-4 p-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
          <span className="text-muted small" style={{ maxWidth: 560 }}>
            Publishing puts every built sheet on the map. Open a sheet to fix it first; failed and unbuilt sheets are
            left out. Discarding removes the drafts; already published sheets stay.
          </span>
          <div className="d-flex gap-2">
            {(c.failed || 0) > 0 && batch.status !== "paused" && (
              <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1" disabled={!!busy} onClick={() => act("retry")}>
                {busy === "retry" ? <Spinner size="sm" /> : <RotateCw size={14} />} Retry {c.failed} failed
              </Button>
            )}
            <Button variant="outline-danger" size="sm" className="d-flex align-items-center gap-1" disabled={!!busy} onClick={() => setConfirm("discard")}>
              {busy === "discard" ? <Spinner size="sm" /> : <Trash2 size={14} />} Discard drafts
            </Button>
            <Button variant="success" className="d-flex align-items-center gap-2" disabled={!!busy || toPublish === 0} onClick={() => setConfirm("publish")}>
              {busy === "publish" ? <Spinner size="sm" /> : <Send size={16} />} Publish {toPublish} built sheet{toPublish === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      <Modal show={!!confirm} onHide={() => setConfirm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">{confirm === "publish" ? `Publish ${toPublish} sheets?` : "Discard the drafts?"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {confirm === "publish" ? (
            <>
              <p className="mb-2">Users with GIS access will see these sheets' layers on the map.</p>
              {doubtfulBuilt.length > 0 && (
                <Form.Check
                  id="leave-out-doubtful"
                  type="checkbox"
                  checked={leaveOutDoubtful}
                  onChange={(e) => setLeaveOutDoubtful(e.target.checked)}
                  label={`Leave out the ${doubtfulBuilt.length} built sheet${doubtfulBuilt.length === 1 ? "" : "s"} marked to check (${doubtfulBuilt
                    .map((s) => s.title)
                    .join(", ")}); they stay drafts`}
                />
              )}
            </>
          ) : (
            "Every draft and staged layer of this bulk import is deleted. Published sheets are not touched."
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant={confirm === "publish" ? "success" : "danger"} onClick={() => act(confirm)}>
            {confirm === "publish" ? "Publish" : "Discard"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-muted">{label}</div>
      <div className="fs-5 fw-semibold">{fmt(value)}</div>
      {sub && <div className="text-muted">{sub}</div>}
    </div>
  );
}
